import {api, RequestParam} from "@/shared/api";
import * as Input from "@/shared/utils/input";

import {BasicDataTable, BasicDataTableConfig, TypedColumnSettings} from "@/shared/ui/basicDataTable"
import * as Constants from "@/shared/constants";
import {FieldObserver} from "@/shared/ui/fieldObserver";
import {BreakModelQueryParams} from "@/shared/queryParams";
import {Combobox} from "@/shared/ui/combobox";
import {FormUtil} from "@/shared/forms/formUtil";
import {WorkScheduleWeekComponent} from "./workScheduleModel/workScheduleWeekComponent";
import {WorkScheduleModelWeekComponent} from "./workScheduleModel/workScheduleModelWeekComponent";
import {htmlEscape} from "@/shared/utils/string";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import {copyOfObject, debounce, serializeObjectToArray} from "@/shared/utils/misc";
import {SecurityStatus} from "@/shared/securityStatus";
import {formatDate, formatDecimalNumber, formatDuration, parseISODate} from "@/shared/utils/datefunctions";
import {BreakModel, ErrorResponse, WorkScheduleModel, WorkScheduleModelWeek, WorkScheduleWeek} from "@/shared/timr";
import {BreakModelSelectionStatus, TargetWorkingTimeType, WorkScheduleModelType} from "@/shared/timrTypes";

export interface WorkScheduleModelsPageConfig {
	breakModels: BreakModel[];
}

interface CheckWorkScheduleModelResponse {
	saveButton: JQuery;
	modals: BootstrapModal.Modal[];
	applyCutsByNormalWorkingTimes: boolean;
}

export class WorkScheduleModelsAdministrationPage {
	private workScheduleModelsTable: WorkScheduleModelsTable;
	private dialogHandler: WorkScheduleModelsDialogHandler;

	constructor(config: WorkScheduleModelsPageConfig) {
		this.workScheduleModelsTable = new WorkScheduleModelsTable({
			element: $("#workScheduleModelsTable"),
			orderable: false,
			allowExpand: true,
			clampNote: true,
			breakModels: config.breakModels,
			initialFilter: this.buildTableFilterData()
		});
		$(this.workScheduleModelsTable)
			.on(BasicDataTable.EVENT_DBLCLICK + " " + WorkScheduleModelsTable.EVENT_WORKSCHEDULEMODEL_EDIT, (event, model: WorkScheduleModel) => {
				this.dialogHandler.setWorkScheduleModel(model);
				this.dialogHandler.openDialog();
			})
			.on(WorkScheduleModelsTable.EVENT_WORKSCHEDULEMODEL_DUPLICATE, (event, model: WorkScheduleModel) => {
				this.dialogHandler.setWorkScheduleModel(model, true);
				this.dialogHandler.openDialog();
			})
			.on(WorkScheduleModelsTable.EVENT_WORKSCHEDULEMODEL_DELETE, (event, model: WorkScheduleModel) => {
				this.deleteWorkScheduleModel(model);
			});

		this.dialogHandler = new WorkScheduleModelsDialogHandler(config.breakModels);
		$(this.dialogHandler)
			.on(WorkScheduleModelsDialogHandler.EVENT_WORK_SCHEDULE_MODEL_UPDATED, () => {
				this.workScheduleModelsTable.reload();
			})
			.on(WorkScheduleModelsDialogHandler.EVENT_WORK_SCHEDULE_MODEL_DELETE, (event, model: WorkScheduleModel) => {
				this.deleteWorkScheduleModel(model);
			});

		$("#workScheduleModelAddButton").click(() => {
			this.dialogHandler.resetWorkScheduleModel();
			this.checkForBreakModelsAndShowDialog(config);
		});

		const filterTable = () => this.workScheduleModelsTable.filter(this.buildTableFilterData());
		$("#workScheduleModelsNameFilter").on("input", debounce(filterTable, this, 350));
		$("#workScheduleModelsFilterArchived").change(() => filterTable());

		const dayLabels = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"].map(day => messages[`workScheduleModel.${day}.short`]);
		$("#dayOrderExplanation").text(`(${dayLabels[globals.firstDayOfWeek]}-${dayLabels[(6 + globals.firstDayOfWeek) % 7]})`);

	}

	private deleteWorkScheduleModel(model: WorkScheduleModel) {
		if (model.userCount && model.userCount > 0) {
			BootstrapModal.warn({
				title: "dialog.warning.title",
				message: messages.withArguments("dialog.delete.workScheduleModel.assignedUsers.message", model.userCount)
			});
			return;
		}

		BootstrapModal.confirm({
			title: "dialog.warning.title",
			message: messages.withArguments("dialog.delete.workScheduleModel.message", model.name),
			yesLabel: "confirmation.delete",
			yesAction: (modal) => {
				api("admin/deleteWorkScheduleModel.json")
					.post({
						workScheduleModelId: model.id
					})
					.then(() => {
						this.workScheduleModelsTable.reload();
						this.dialogHandler.close();
						modal.close();
					})
					.always(() => {
						this.dialogHandler.resetLoadingButtons();
						modal.resetLoadingButtons();
					});
			},
			noAction: (modal) => {
				this.dialogHandler.resetLoadingButtons();
				modal.close();
			}
		});
	}

	private buildTableFilterData() {
		return [
			{
				name: "name",
				value: $("#workScheduleModelsNameFilter").val() as string
			},
			{
				name: "archived",
				value: $("#workScheduleModelsFilterArchived").val() as string
			}
		];
	}

	private checkForBreakModelsAndShowDialog(config: WorkScheduleModelsPageConfig) {
		if (config.breakModels.length === 0) {
			BootstrapModal.info({
				size: "modal-lg",
				title: "workScheduleModel.noBreakModelDefined.title",
				message: "workScheduleModel.noBreakModelDefined.message",
				buttons: [
					{
						text: messages["workScheduleModel.noBreakModelDefined.continue"],
						css: "btn-default",
						action: (modal) => {
							modal.close();
							this.dialogHandler.openDialog();
						}
					},
					{
						text: messages["workScheduleModel.noBreakModelDefined.addNow"],
						css: "btn-primary",
						action: () => {
							window.location.href = BreakModelQueryParams.buildLink({}, "admin/breakModels.html", "addBreakModel");
						}
					}
				]
			});
		} else {
			this.dialogHandler.openDialog();
		}
	}
}

interface WorkScheduleModelsTableConfig extends BasicDataTableConfig {
	breakModels: BreakModel[];
}

class WorkScheduleModelsTable extends BasicDataTable<WorkScheduleModel> {
	public static EVENT_WORKSCHEDULEMODEL_EDIT = "eventModelEdit";
	public static EVENT_WORKSCHEDULEMODEL_DUPLICATE = "eventModelDuplicate";
	public static EVENT_WORKSCHEDULEMODEL_DELETE = "eventModelDelete";

	constructor(public config: WorkScheduleModelsTableConfig) {
		super("/timr/admin/workScheduleModels.json", config);
	}

	protected createDataTableConfig(): DataTables.Settings {
		return {
			dom: `rt<"datatable-footer"ip>`,
			paging: true,
			serverSide: true,
			rowCallback: (row: Node, data: object) => {
				const recording = data as WorkScheduleModel;
				if (recording.archived) {
					$(row).addClass("archived");
				}
			},
			pageLength: Constants.ADMINISTRATION_MAIN_TABLES_PAGE_LENGTH
		};
	}

	protected createContextMenu() {
		return super.createContextMenu()
			.addMenuEntry({
				label: "workScheduleModels.context.edit",
				action: (model) => {
					$(this).triggerHandler(WorkScheduleModelsTable.EVENT_WORKSCHEDULEMODEL_EDIT, model);
				}
			})
			.addMenuEntry({
				label: "workScheduleModels.context.duplicate",
				action: (model) => {
					$(this).triggerHandler(WorkScheduleModelsTable.EVENT_WORKSCHEDULEMODEL_DUPLICATE, model);
				}
			})
			.addMenuEntry({
				label: "workScheduleModels.context.delete",
				action: (model) => {
					$(this).triggerHandler(WorkScheduleModelsTable.EVENT_WORKSCHEDULEMODEL_DELETE, model);
				}
			})
			.addMenuEntry({
				label: "workScheduleModels.context.unarchive",
				show: (model: WorkScheduleModel) => model.archived,
				action: (model: WorkScheduleModel) => this.unarchiveWorkScheduleModel(model)
			})
			.addMenuEntry({
				label: "workScheduleModels.context.archive",
				show: (model: WorkScheduleModel) => !model.archived,
				action: (model: WorkScheduleModel) => this.archiveWorkScheduleModel(model)
			});
	}

	protected getColumnSettings(): TypedColumnSettings<WorkScheduleModel>[] {
		return [
			{data: "id", width: "44px", orderable: false, className: "context-menu", render: this.contextMenu.render},
			{data: "name", orderable: true, render: BasicDataTable.renderText},
			{data: "userCount", orderable: false, render: BasicDataTable.renderNumber},
			{
				data: "id",
				orderable: false,
				render: (data, type, model, meta) => this.renderTotalWorkDuration(data, type, model, meta),
				createdCell: (td, c, r: WorkScheduleModel) => r.workScheduleModelType === WorkScheduleModelType.ALTERNATING_WEEKS ? $(td).attr("colspan", "2").addClass("text-center") : null
			},
			{
				data: "id",
				width: "340px",
				orderable: false,
				render: (data, type, model, meta) => this.renderDailyWorkDuration(data, type, model, meta),
				createdCell: (td, c, r: WorkScheduleModel) => {
					if (r.workScheduleModelType === WorkScheduleModelType.ALTERNATING_WEEKS) {
						$(td).remove();
					}
				}
			},
			{
				data: "breakModelId",
				orderable: false,
				render: (data, type, model, meta) => this.renderBreakModelName(data, type, model, meta)
			},
			{
				data: "archived",
				orderable: false,
				render: this.renderArchived,
				className: "slim-icon-column",
				width: BasicDataTable.COLUMN_SIZE_STATUS_ICON
			}
		];
	}

	protected getDefaultOrder(): [number, string][] {
		return [[1, "asc"]];
	}

	private renderBreakModelName(data: number, _type: string, model: WorkScheduleModel, _meta: unknown) {
		if (model.breakModelSelectionStatus === BreakModelSelectionStatus.NONE ||
			(model.breakModelSelectionStatus == BreakModelSelectionStatus.RULE_SELECTED && !model.breakModelId)
		) {
			return `<span class="text-orange-600"><i class="fas fa-triangle-exclamation position-left"></i> ${messages["admin.workScheduleModel.noBreakModel.none"]}</span>`;
		} else if (model.breakModelSelectionStatus === BreakModelSelectionStatus.EXPLICITLY_NONE) {
			return messages["admin.workScheduleModel.noBreakModel.explicitlyNone"];
		} else {
			const models = this.config.breakModels.filter(model => model.id === data);
			return models[0]
				? htmlEscape(models[0].name)
				: "";
		}
	}

	private renderTotalWorkDuration(this: void, _data: number, _type: string, model: WorkScheduleModel, _meta: unknown) {
		return model.workScheduleModelType === WorkScheduleModelType.FIXED_WEEK ?
			formatDuration(getTotalWorkDuration(model.workScheduleWeek)) :
			messages.withArguments("workScheduleModel.alternating.count", model.workScheduleModelWeeks?.length);
	}

	private renderDailyWorkDuration(this: void, _data: number, _type: string, model: WorkScheduleModel, _meta: unknown) {
		// First day of week: 0=Sunday, 1=Monday, ...
		const week = model.workScheduleWeek;
		const times = [week.workDurationSunday, week.workDurationMonday, week.workDurationTuesday, week.workDurationWednesday,
			week.workDurationThursday, week.workDurationFriday, week.workDurationSaturday];

		// Roll over array as ring buffer have correct day ordering according to client setting
		const last = times.splice(0, globals.firstDayOfWeek);
		return [...times, ...last].map(duration => "<span style='display: inline-block; width: 40px; text-align: right'>" + formatDuration(duration) + "</span>").join(" ");
	}

	private renderArchived(this: void, data: boolean, _type: string, _model: WorkScheduleModel, _meta: unknown): string {
		return data ? `<i class="fal fa-archive" title="${messages["workScheduleModel.archived"]}"></i>` : "";
	}

	private archiveWorkScheduleModel(model: WorkScheduleModel) {
		api("admin/archiveWorkScheduleModel.json")
			.post({
				workScheduleModelId: model.id
			})
			.then(() => {
				this.reload();
			});
	}

	private unarchiveWorkScheduleModel(model: WorkScheduleModel) {
		api("admin/unarchiveWorkScheduleModel.json")
			.post({
				workScheduleModelId: model.id
			})
			.then(() => {
				this.reload();
			});
	}
}

class WorkScheduleModelsDialogHandler {
	public static EVENT_WORK_SCHEDULE_MODEL_UPDATED = "eventModelUpdated";
	public static EVENT_WORK_SCHEDULE_MODEL_DELETE = "eventModelDelete";

	private readonly modelDialog: BootstrapModal.Modal;
	private model: WorkScheduleModel;

	private observer: FieldObserver<WorkScheduleModel>;
	private checkForWorkScheduleModelChange = false;
	private checkForBreakModelChange = false;
	private oldBreakModelId: number = null;
	private oldBreakModelSelectionStatus: BreakModelSelectionStatus = null;

	private breakModelCombobox: Combobox<BreakModel>;
	private workScheduleModelWeekInputFixedWeek = document.getElementById("workScheduleModelWeekInputFixedWeek") as WorkScheduleWeekComponent;
	private workScheduleModelAlternatingWeeksContainer = $("#workScheduleModelAlternatingWeeksContainer");
	private workScheduleModelAddAlternatingWeekElement = $("#workScheduleModelAddAlternatingWeek");
	private workScheduleModelWeeks: WorkScheduleModelWeekComponent[] = [];

	constructor(breakModels: BreakModel[]) {
		this.modelDialog = new BootstrapModal.Modal({
			title: "workScheduleModel.header.create",
			message: $("#workScheduleModelDialog"),
			destroyOnClose: false,
			size: "modal-lg",
			css: {
				content: "pt-10 scrollable-modal"
			},
			buttons: [{
				text: "dialog.save",
				css: "btn-primary",
				action: () => {
					this.checkWorkScheduleModel()
						.then(response => this.saveWorkScheduleModel(response))
						.fail((response: { saveButton: JQuery }) => Button.resetLoading(response.saveButton));
				}
			}, {
				text: "dialog.cancel"
			}, {
				text: "dialog.delete",
				css: "btn-outline-danger",
				left: true,
				action: () => {
					$(this).triggerHandler(WorkScheduleModelsDialogHandler.EVENT_WORK_SCHEDULE_MODEL_DELETE, this.model);
				}
			}],
			onOpen: () => {
				$("#workScheduleModelName").focus();
			}
		});

		this.observer = new FieldObserver<WorkScheduleModel>();
		this.observer.onFieldValueChange((event, data) => {
			if (data.field !== "name" && data.field !== "projectTimeBookingRatio" && data.field !== "breakModelId" && data.field !== "breakModelSelectionStatus") {
				// check if the work schedule model can be changed if fields other than name and projectTimeBookingRatio are changed
				this.checkForWorkScheduleModelChange = true;
			}
			if (data.field === "breakModelId" || data.field === "breakModelSelectionStatus") {
				const shouldValidateChange = !(data.newValue === BreakModelSelectionStatus.EXPLICITLY_NONE && this.oldBreakModelSelectionStatus === BreakModelSelectionStatus.NONE);
				const breakModelIdChanged = this.oldBreakModelId !== this.getBreakModelId();
				this.checkForBreakModelChange = shouldValidateChange && breakModelIdChanged;
			}
			this.updateFieldValues();
		});

		this.breakModelCombobox = new Combobox<BreakModel>({
			items: breakModels,
			clearable: false,
			formatter: model => model.name,
			idElement: $("#breakModelId"),
			filterElement: $("#breakModelFilter"),
			caretElement: $("#breakModelFilterCaret")
		});

		this.resetWorkScheduleModel();
		this.updateFieldValues();
		this.initListener();
	}

	public resetWorkScheduleModel() {
		this.setWorkScheduleModel({
			id: null,
			workScheduleModelType: WorkScheduleModelType.FIXED_WEEK,
			breakModelSelectionStatus: BreakModelSelectionStatus.RULE_SELECTED,
			breakModelId: null,
			version: 0,
			name: "",
			archived: false,
			workScheduleWeek: {
				workDurationMonday: 0,
				workDurationTuesday: 0,
				workDurationWednesday: 0,
				workDurationThursday: 0,
				workDurationFriday: 0,
				workDurationSaturday: 0,
				workDurationSunday: 0
			},
			requiresCustomVacationEnabled: false,
			requiresCustomVacationOnMonday: false,
			requiresCustomVacationOnTuesday: false,
			requiresCustomVacationOnWednesday: false,
			requiresCustomVacationOnThursday: false,
			requiresCustomVacationOnFriday: false,
			requiresCustomVacationOnSaturday: false,
			requiresCustomVacationOnSunday: false,
			projectTimeBookingRatio: null,
			targetWorkingTimeType: TargetWorkingTimeType.DAILY_TARGET_HOURS,
			flexibleWorkDays: false
		});
	}

	public setWorkScheduleModel(model: WorkScheduleModel, duplicate: boolean = false) {
		this.model = copyOfObject(model);
		this.oldBreakModelId = this.model.breakModelId;
		this.oldBreakModelSelectionStatus = this.model.breakModelSelectionStatus;

		if (duplicate) {
			this.model.id = null;
			this.model.name += " " + messages["text.duplicate"];
			this.model.version = 0;
			this.model.userCount = 0;
		}

		// If no BM is selected, set the status to RULE_SELECTED as default (TIMR-10862)
		this.model.breakModelSelectionStatus = this.model.breakModelSelectionStatus === BreakModelSelectionStatus.NONE
			? BreakModelSelectionStatus.RULE_SELECTED
			: this.model.breakModelSelectionStatus;
		this.observer.setModel(this.model);

		this.updateTitle(duplicate);
		this.updateFieldValues();

		this.initWorkScheduleWeeks();

		FormUtil.removeAllFeedback($("#workScheduleModelForm"));
	}

	private initWorkScheduleWeeks() {
		this.workScheduleModelWeekInputFixedWeek.workScheduleWeek = this.model.workScheduleWeek;
		this.workScheduleModelWeekInputFixedWeek.targetWorkingTimeType = this.model.targetWorkingTimeType;

		this.workScheduleModelAddAlternatingWeekElement.toggle(!this.model.userCount);

		// fixed week: mark changes on user edits, update sums on any update
		this.workScheduleModelWeekInputFixedWeek.addEventListener(WorkScheduleWeekComponent.EVENT_CHANGED, () => {
			this.checkForWorkScheduleModelChange = true;
			this.updateSumAndAverage();
		});
		this.workScheduleModelWeekInputFixedWeek.addEventListener(WorkScheduleWeekComponent.EVENT_UPDATED, () => {
			this.updateSumAndAverage();
		});

		this.workScheduleModelWeeks = [];
		if (this.model.workScheduleModelWeeks?.length > 0) {
			// Create week components for existing alternating weeks
			this.model.workScheduleModelWeeks?.forEach((week) => {
				this.createAndAppendWorkScheduleModelWeekComponent(week);
			});
		} else {
			// Create one default week if none exist and expand it
			this.createAndAppendWorkScheduleModelWeekComponent().expand();
		}
	}

	private createAndAppendWorkScheduleModelWeekComponent(week?: WorkScheduleModelWeek): WorkScheduleModelWeekComponent {
		const newComponent = document.createElement("work-schedule-model-week") as WorkScheduleModelWeekComponent;
		newComponent.setTargetWorkingTimeType(this.model.targetWorkingTimeType);
		if (week) {
			newComponent.setWorkScheduleModelWeek(week);
		}
		newComponent.addEventListener(WorkScheduleModelWeekComponent.EVENT_CHANGED, () => {
			this.checkForWorkScheduleModelChange = true;
			this.updateSumAndAverage();
		});
		newComponent.addEventListener(WorkScheduleModelWeekComponent.EVENT_UPDATED, () => {
			this.updateSumAndAverage();
		});
		newComponent.addEventListener(WorkScheduleModelWeekComponent.EVENT_REMOVED, () => {
			this.checkForWorkScheduleModelChange = true;
			const index = this.workScheduleModelWeeks.indexOf(newComponent);
			if (index > -1) {
				this.workScheduleModelWeeks.splice(index, 1);
				this.renderWorkScheduleModelWeekComponents();
			}
		});
		this.workScheduleModelWeeks.push(newComponent);
		this.renderWorkScheduleModelWeekComponents();
		return newComponent;
	}

	private renderWorkScheduleModelWeekComponents() {
		this.workScheduleModelAlternatingWeeksContainer.children().remove();
		for (let i = 0; i < this.workScheduleModelWeeks.length; i++) {
			this.workScheduleModelWeeks[i].setWeekNumber(i);
			this.workScheduleModelWeeks[i].setRemovable(!this.model.userCount && this.workScheduleModelWeeks.length > 1);
			if (i > 0) {
				this.workScheduleModelAlternatingWeeksContainer.append("<hr>");
			}
			this.workScheduleModelAlternatingWeeksContainer.append(this.workScheduleModelWeeks[i]);
		}
	}

	public openDialog() {
		// reset needsCheck flag when opening the dialog
		this.checkForWorkScheduleModelChange = false;
		this.checkForBreakModelChange = false;
		this.modelDialog.show();
	}

	public close() {
		this.modelDialog.close();
	}

	public resetLoadingButtons() {
		this.modelDialog.resetLoadingButtons();
	}

	private checkWorkScheduleModel(): JQueryPromise<CheckWorkScheduleModelResponse> {
		const deferred = $.Deferred<{
			saveButton: JQuery,
			modals: BootstrapModal.Modal[],
			applyCutsByNormalWorkingTimes: boolean
		}>();

		const button = this.modelDialog.getButtons("btn-primary");
		Button.setLoading(button);

		if (this.model.id && (this.checkForWorkScheduleModelChange || this.checkForBreakModelChange)) {
			const checkData = this.modelToArray();

			api("admin/checkForWorkScheduleModelChange.json")
				.post(checkData)
				.then((data) => {
					if (data.overlappingWorkingTimeRequestsCount === 0 && data.overlappingWorkingTimeDateSpansCount === 0 &&
						data.workingTimesForRequests === 0 && data.changeableWorkingTimesForRequests === 0 &&
						data.workingTimesForWorkScheduleModel === 0 && data.workingTimeDateSpansAfterLastTimeAccountBalance === 0) {
						deferred.resolve({
							saveButton: button,
							applyCutsByNormalWorkingTimes: false,
							modals: [this.modelDialog]
						});
					} else if (data.overlappingWorkingTimeRequestsCount > 0) {
						// block work schedule model update: there are approved requests where the WorkingTime recordings are not changeable anymore
						let extraMsg = "";
						for (const wtr of data.overlappingWorkingTimeRequests) {
							extraMsg += messages.withArguments(
								"dialog.update.userWorkScheduleModel.overlappingWorkingTimeRequests.message.overlappingRecord",
								htmlEscape(wtr.user.fullname),
								formatDate(parseISODate(wtr.user.lastTimeAccountBalance)),
								messages[`workingTimeTypeCategory.${wtr.workingTimeType.category}`],
								formatDate(parseISODate(wtr.start)),
								formatDate(parseISODate(wtr.end))
							);
						}
						BootstrapModal.warn({
							title: "dialog.update.userWorkScheduleModel.foundVacations.title",
							message: messages.withArguments("dialog.update.userWorkScheduleModel.overlappingWorkingTimeRequests.message", extraMsg),
							escapeText: false,
							size: "modal-lg",
							onClose: () => deferred.reject({saveButton: button})
						});
					} else if (data.overlappingWorkingTimeDateSpansCount > 0) {
						// block work schedule model update: there are approved requests where the WorkingTime recordings are not changeable anymore
						BootstrapModal.warn({
							title: "dialog.update.userWorkScheduleModel.foundVacations.title",
							message: "dialog.update.userWorkScheduleModel.overlappingWorkingTimeDateSpans.message",
							escapeText: false,
							size: "modal-lg",
							onClose: () => deferred.reject({saveButton: button})
						});
					} else if (data.workingTimesForRequests > data.changeableWorkingTimesForRequests) {
						// block work schedule model update: there are approved requests where the WorkingTime recordings are not changeable anymore
						BootstrapModal.warn({
							title: "dialog.update.workScheduleModel.foundVacations.title",
							message: "dialog.update.userWorkScheduleModel.foundVacations.message",
							onClose: () => deferred.reject({saveButton: button})
						});
					} else if (data.workDaysChanged && (data.workingTimesForWorkScheduleModel > 0 || data.workingTimeDateSpansAfterLastTimeAccountBalance > 0)) {
						// block work schedule model update: the work days changed and there are half/full day WorkingTime recordings for the new work schedule model
						BootstrapModal.warn({
							title: "dialog.update.userWorkScheduleModel.foundVacations.title",
							message: "dialog.update.userWorkScheduleModel.message",
							onClose: () => deferred.reject({saveButton: button})
						});
					} else if (data.workingTimesForRequests > 0) {
						// work model update is allowed but a confirmation dialog has to be shown (changeable working times for requests)

						BootstrapModal.confirm({
							title: "dialog.update.workScheduleModel.title",
							message: "dialog.update.workScheduleModel.adaptRequests.message",
							yesLabel: "dialog.update.workScheduleModel.buttonChange",
							noLabel: "dialog.update.userWorkScheduleModel.no",
							yesAction: (modal: BootstrapModal.Modal) => {
								deferred.resolve({
									saveButton: button,
									applyCutsByNormalWorkingTimes: false,
									modals: [this.modelDialog]
								});
								modal.close();
							},
							noAction: (modal: BootstrapModal.Modal) => {
								deferred.reject({saveButton: button});
								modal.close();
							},
							escapeText: false,
							destroyOnClose: true
						});
					} else if (data.workingTimesForWorkScheduleModel > 0) {
						// work model update is allowed but a confirmation dialog has to be shown (not-yet balanced half/full day WorkingTime without request)
						BootstrapModal.confirm({
							title: "dialog.update.workScheduleModel.title",
							message: "dialog.update.workScheduleModel.message",
							yesLabel: "dialog.update.workScheduleModel.yes",
							noLabel: "confirmation.cancel",
							yesAction: (modal: BootstrapModal.Modal) => {
								deferred.resolve({
									saveButton: button,
									applyCutsByNormalWorkingTimes: false,
									modals: [this.modelDialog]
								});
								modal.close();
							},
							noAction: () => deferred.reject({saveButton: button}),
						});
					} else {
						BootstrapModal.error({message: "dialog.message.workschedulemodel.error"});
					}
				})
				.fail((response) => this.handleErrorResponse(response, button));
		} else {
			deferred.resolve({
				saveButton: button,
				applyCutsByNormalWorkingTimes: false,
				modals: [this.modelDialog]
			});
		}

		return deferred.promise();
	}

	private saveWorkScheduleModel(changeResponse: CheckWorkScheduleModelResponse) {
		FormUtil.removeAllFeedback($("#workScheduleModelForm"));

		this.updateWorkScheduleModelForSave();

		const modelData: RequestParam[] = this.modelToArray();
		modelData.push({
			name: "applyCutsByNormalWorkingTimes",
			value: changeResponse.applyCutsByNormalWorkingTimes
		});

		api("admin/saveWorkScheduleModel.json")
			.post(modelData)
			.then(() => {
				changeResponse.modals.forEach(modal => modal.close());
				$(this).triggerHandler(WorkScheduleModelsDialogHandler.EVENT_WORK_SCHEDULE_MODEL_UPDATED);
			})
			.fail((response) => this.handleErrorResponse(response, changeResponse.saveButton));
	}

	private modelToArray() {
		// Prepare model with alternating weeks if applicable
		const modelToSerialize = {...this.model};

		if (this.model.workScheduleModelType === WorkScheduleModelType.ALTERNATING_WEEKS) {
			// Build workScheduleModelWeeks array from components with proper preprocessing
			modelToSerialize.workScheduleModelWeeks = this.workScheduleModelWeeks.map((weekComponent, index) => {
				const workScheduleModelWeek = weekComponent.getWorkScheduleModelWeek();
				workScheduleModelWeek.weekNumber = index;
				if (this.model.targetWorkingTimeType !== TargetWorkingTimeType.NORMAL_WORKING_TIME) {
					this.resetNormalWorkingTimeFields(workScheduleModelWeek.workScheduleWeek);
				}
				return workScheduleModelWeek;
			});
		}

		// Use deep serialization to automatically handle all nested objects and arrays
		return serializeObjectToArray(modelToSerialize);
	}

	private updateWorkScheduleModelForSave() {
		// Set work schedule week fields from fixed week component
		this.model.workScheduleWeek = this.workScheduleModelWeekInputFixedWeek?.workScheduleWeek;
		this.updateTargetWorkingTimeTypeFieldsForSave();
		this.updateBreakModelFieldForSave();
	}

	/**
	 * Updates the fields according to the TargetWorkingTimeType before saving, in case values are entered and the type is changed afterwards
	 * e.g. the type is DAILY_TARGET and tuesday got 7h, then the type is changed to NORMAL_WORKING_TIME (the value remains in the now disabled field)
	 * However, the ui target fields must not be saved, because in this case these only serve as info
	 * If isRecordingRestrictionsAllowed is false NORMAL_WORKING_TIME is not available, just the targetWorkingTimeType will be set to DAILY_TARGET_HOURS
	 * @private
	 */
	private updateTargetWorkingTimeTypeFieldsForSave() {
		if (!SecurityStatus.isRecordingRestrictionsAllowed() && !SecurityStatus.isPaidTimeOffCompensationAllowed()) {
			this.model.targetWorkingTimeType = TargetWorkingTimeType.DAILY_TARGET_HOURS;
			return;
		}

		if (this.model.targetWorkingTimeType !== TargetWorkingTimeType.NORMAL_WORKING_TIME) {
			this.resetNormalWorkingTimeFields(this.model.workScheduleWeek);
		}
	}

	private updateBreakModelFieldForSave() {
		// If the status is not RULE_SELECTED, clear the combobox selection
		// as no break model can be selected in this case.
		// We only clear it at save time, so that the user can change the status
		// and come back to RULE_SELECTED without having to re-select the break model
		if (this.model.breakModelSelectionStatus !== BreakModelSelectionStatus.RULE_SELECTED) {
			this.breakModelCombobox.clearSelection();
		}
	}

	private resetNormalWorkingTimeFields(workScheduleWeek: WorkScheduleWeek) {
		Constants.WEEKDAYS.forEach(weekday => {
			workScheduleWeek[`normalWorkingTime1Start${weekday}`] = "";
			workScheduleWeek[`normalWorkingTime1End${weekday}`] = "";
			workScheduleWeek[`normalWorkingTime2Start${weekday}`] = "";
			workScheduleWeek[`normalWorkingTime2End${weekday}`] = "";
		});
	}

	private initListener() {
		this.observer.observeInput("#workScheduleModelName", "name");
		this.observer.observeInput("#projectTimeBookingRatio", "projectTimeBookingRatio", FieldObserver.getDecimalNumberFromField);
		this.observer.observeInput("#flexibleWorkDays", "flexibleWorkDays", FieldObserver.getCheckboxState);
		this.observer.observeInput('input[name="breakModelSelectionStatus"]', "breakModelSelectionStatus", () => $('input[name="breakModelSelectionStatus"]:checked').val() as BreakModelSelectionStatus);

		this.observer.observeInput("#workScheduleModelType", "workScheduleModelType", () => $("#workScheduleModelType").val() as WorkScheduleModelType);
		this.observer.observeInput(`#workScheduleModelVacationDaysEqualsWorkingDays`, "requiresCustomVacationEnabled", () => !$("#workScheduleModelVacationDaysEqualsWorkingDays").is(":checked"));
		for (const day of Constants.WEEKDAYS) {
			this.observer.observeInput(`#requiresCustomVacationOn${day}`, `requiresCustomVacationOn${day}`, FieldObserver.getCheckboxState);
		}

		$(this.breakModelCombobox).on(Combobox.EVENT_CHANGED, () => {
			this.model.breakModelId = this.getBreakModelId();
			this.checkForBreakModelChange = this.oldBreakModelId !== this.model.breakModelId;
		});
		if (SecurityStatus.isRecordingRestrictionsAllowed() || SecurityStatus.isPaidTimeOffCompensationAllowed()) {
			this.observer.observeInput("#workScheduleModelTargetWorkingTimeType", "targetWorkingTimeType", checkbox => {
				const dailyTargetChecked = $(checkbox).is(":checked");
				return dailyTargetChecked ? TargetWorkingTimeType.NORMAL_WORKING_TIME : TargetWorkingTimeType.DAILY_TARGET_HOURS;
			});
		}

		this.workScheduleModelAddAlternatingWeekElement
			.click((e) => {
				this.createAndAppendWorkScheduleModelWeekComponent().expand();
				this.checkForWorkScheduleModelChange = true;
				e.currentTarget.scrollIntoView();
			});
	}

	private getBreakModelId(): number {
		const breakModel = this.breakModelCombobox.getSelectedItem();
		return breakModel ? breakModel.id : null;
	}

	/**
	 * Updates the UI elements related to break model selection status
	 * @private
	 */
	private updateBreakModelSelectionUI(): void {
		this.breakModelCombobox.setDisabled(this.model.breakModelSelectionStatus !== BreakModelSelectionStatus.RULE_SELECTED);
		$("#breakModelSelectionStatus_EXPLICITLY_NONE_info").toggle(this.model.breakModelSelectionStatus === BreakModelSelectionStatus.EXPLICITLY_NONE);
	}

	private updateFieldValues() {
		this.modelDialog.toggleButton("btn-outline-danger", this.model.id != null);

		$("#workScheduleModelId").val(this.model.id);
		$("#workScheduleModelVersion").val(this.model.version);
		$("#workScheduleModelName").val(this.model.name || "");

		const workScheduleModelTypeElement = $("#workScheduleModelType");
		workScheduleModelTypeElement.val(this.model.workScheduleModelType);
		Input.toggle(workScheduleModelTypeElement, !this.model.userCount);

		$("#projectTimeBookingRatio").val(formatDecimalNumber(this.model.projectTimeBookingRatio));
		$("[data-visible-for-type]").hide().closest(`[data-visible-for-type="${this.model.workScheduleModelType}"]`).show();

		$(`input[name="breakModelSelectionStatus"][value="${this.model.breakModelSelectionStatus}"]`).prop("checked", true);
		this.updateBreakModelSelectionUI();
		if (this.model.breakModelId) {
			this.breakModelCombobox.setSelectedItemById(this.model.breakModelId);
		} else {
			this.breakModelCombobox.clearSelection();
		}
		if (SecurityStatus.isRecordingRestrictionsAllowed() || SecurityStatus.isPaidTimeOffCompensationAllowed()) {
			const normalWorkingTimeTargetType = this.model.targetWorkingTimeType === TargetWorkingTimeType.NORMAL_WORKING_TIME;
			$("#workScheduleModelTargetWorkingTimeType").prop("checked", normalWorkingTimeTargetType);
			this.workScheduleModelWeekInputFixedWeek.targetWorkingTimeType = this.model.targetWorkingTimeType;
			for (const weekComponent of this.workScheduleModelWeeks) {
				weekComponent.setTargetWorkingTimeType(this.model.targetWorkingTimeType);
			}
		}
		this.updateSumAndAverage();
		this.updateRequireCustomVacationUI();
		$("#flexibleWorkDays").prop("checked", this.model.flexibleWorkDays);
	}

	updateSumAndAverage() {
		$("#workScheduleModelSum").text(formatDuration(this.workScheduleModelWeekInputFixedWeek.totalWorkDuration));
		const totalDurationSum = this.workScheduleModelWeeks.reduce((sum, week) => sum + week.getTotalWorkDuration(), 0);
		$("#workScheduleModelAverage").text(formatDuration(totalDurationSum / this.workScheduleModelWeeks.length));
	}

	private updateTitle(duplicate: boolean = false) {
		let message: string;
		if (!duplicate) {
			message = messages[this.model.id == null ? "workScheduleModel.header.create" : "workScheduleModel.header.edit"];
		} else {
			message = messages["workScheduleModel.header.duplicate"];
		}
		this.modelDialog.setTitle(message, true);
	}

	/**
	 * Resets button loading and provides feedback when errors occur.
	 * Since there is less space available due to the many time range fields,
	 * errors are grouped and indicated by a short marker showing all error messages by a title/tooltip
	 * @param response
	 * @param button
	 * @private
	 */
	private handleErrorResponse(response: ErrorResponse, button: JQuery) {
		Button.resetLoading(button);
		if (response.responseJSON?.fieldErrors) {
			FormUtil.buildShortMessageFeedback($("#workScheduleModelForm"), response.responseJSON.fieldErrors, "^normalWorkingTime", false);
		}
	}

	private updateRequireCustomVacationUI() {
		$("#workScheduleModelVacationDaysEqualsWorkingDays").prop("checked", !this.model.requiresCustomVacationEnabled);
		for (const day of Constants.WEEKDAYS) {
			$(`#requiresCustomVacationOn${day}`).prop("checked", this.model[`requiresCustomVacationOn${day}`]);
			$("#customVacationRelevantDaysContainer").toggle(this.model.requiresCustomVacationEnabled);
		}
	}
}

function getTotalWorkDuration(week: WorkScheduleWeek): number {
	return week.workDurationMonday + week.workDurationTuesday
		+ week.workDurationWednesday + week.workDurationThursday
		+ week.workDurationFriday + week.workDurationSaturday
		+ week.workDurationSunday;
}
